import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { generateMatchRosterPDF } from '../utils/pdfRoster';
import api from '../services/api';
import { useTableSort } from '../hooks/useTableSort';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';
import { isAdminOrSuperAdmin } from '../utils/auth';

interface LeagueStandingsTableProps {
  standings: Array<{
    teamId: string;
    teamName: string;
    wins: number;
    losses: number;
    draws: number;
    goalsFor: number;
    goalsAgainst: number;
    points: number;
    goalDifference: number;
  }>;
  teamId: string;
  leagueName?: string;
}

const LeagueStandingsTable: React.FC<LeagueStandingsTableProps> = ({ standings, teamId, leagueName }) => {
  const { sortedData, handleSort, getSortIcon } = useTableSort(standings, { key: 'points', direction: 'desc' });

  return (
    <div style={{ marginTop: '20px' }}>
      <h3 style={{ marginBottom: '15px' }}>
        League Standings - {leagueName}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', userSelect: 'none' }}>Pos</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('teamName' as any)}>
                Team {getSortIcon('teamName' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('wins' as any)}>
                W {getSortIcon('wins' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('losses' as any)}>
                L {getSortIcon('losses' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('draws' as any)}>
                D {getSortIcon('draws' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('goalsFor' as any)}>
                GF {getSortIcon('goalsFor' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('goalsAgainst' as any)}>
                GA {getSortIcon('goalsAgainst' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('goalDifference' as any)}>
                GD {getSortIcon('goalDifference' as any)}
              </th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('points' as any)}>
                Pts {getSortIcon('points' as any)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((standing, pos) => (
              <tr 
                key={standing.teamId}
                style={standing.teamId === teamId ? { background: '#E8F5E9', fontWeight: 'bold' } : {}}
              >
                <td>{pos + 1}</td>
                <td>
                  {standing.teamName}
                  {standing.teamId === teamId && ' (Your Team)'}
                </td>
                <td>{standing.wins}</td>
                <td>{standing.losses}</td>
                <td>{standing.draws}</td>
                <td>{standing.goalsFor}</td>
                <td>{standing.goalsAgainst}</td>
                <td>{standing.goalDifference > 0 ? '+' : ''}{standing.goalDifference}</td>
                <td><strong>{standing.points}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface TeamInfo {
  team: {
    id: string;
    name: string;
    teamNumber: string;
    leagueId?: string;
    league?: {
      id: string;
      name: string;
    };
  };
  role: string;
  position?: string;
  nextGame?: {
    id: string;
    scheduledDate: string;
    scheduledTime: string;
    location: string;
    homeTeam: { id: string; name: string };
    awayTeam: { id: string; name: string };
    isHome: boolean;
    opponent: string;
  };
  playerStats?: {
    matchesPlayed: number;
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
  };
  teamStats?: {
    wins: number;
    losses: number;
    draws: number;
    goalsFor: number;
    goalsAgainst: number;
    points: number;
    goalDifference: number;
  };
  leagueStandings?: Array<{
    teamId: string;
    teamName: string;
    wins: number;
    losses: number;
    draws: number;
    goalsFor: number;
    goalsAgainst: number;
    points: number;
    goalDifference: number;
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [teamsInfo, setTeamsInfo] = useState<TeamInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserTeams();
    }
  }, [user]);

  const fetchUserTeams = async () => {
    if (!user) return;

    try {
      // Fetch shared data ONCE up front (instead of per-team)
      const [teamsRes, matchesRes, playerRes, leaguesRes] = await Promise.all([
        api.get('/teams', { params: { page: '1', limit: '100' } }),
        api.get('/matches', { params: { page: '1', limit: '100' } }),
        user.profileType === 'player'
          ? api.get(`/players/${user.id}`).catch(() => ({ data: { teams: [], statistics: [] } }))
          : Promise.resolve({ data: { teams: [], statistics: [] } }),
        api.get('/leagues', { params: { page: '1', limit: '100' } }),
      ]);

      const allTeams = teamsRes.data.teams || teamsRes.data || [];
      const allMatches = matchesRes.data.matches || matchesRes.data || [];
      const playerTeams: any[] = playerRes.data.teams || [];
      const playerStats: any[] = playerRes.data.statistics || [];
      const allLeagues = leaguesRes.data.leagues || leaguesRes.data || [];

      // Build lookup maps
      const teamMap = new Map<string, any>();
      allTeams.forEach((t: any) => teamMap.set(t.id, t));

      const leagueCache = new Map<string, any>();
      // Fetch league details (with standings) only for unique league IDs the user needs
      const userLeagueIds = new Set<string>();

      // Determine user's teams: from player memberships + managed teams
      interface UserTeamEntry { team: any; role: string; position?: string; isManager?: boolean }
      const userTeamEntries: UserTeamEntry[] = [];

      // Teams where user is a manager
      allTeams.forEach((team: any) => {
        if (team.managerId === user.id || team.manager?.id === user.id) {
          userTeamEntries.push({ team, role: 'manager', isManager: true });
          if (team.leagueId) userLeagueIds.add(team.leagueId);
        }
      });

      // Teams where user is a member (from player profile - single API call already done)
      const managerTeamIds = new Set(userTeamEntries.map(e => e.team.id));
      playerTeams.forEach((membership: any) => {
        if (managerTeamIds.has(membership.teamId)) return; // already added as manager
        const team = teamMap.get(membership.teamId);
        if (team) {
          userTeamEntries.push({ team, role: membership.role || 'player', position: membership.position });
          if (team.leagueId) userLeagueIds.add(team.leagueId);
        }
      });

      // Fetch league details (with standings) for all needed leagues in parallel
      const leaguePromises = Array.from(userLeagueIds).map(async (lid) => {
        try {
          const res = await api.get(`/leagues/${lid}`);
          leagueCache.set(lid, res.data);
        } catch { /* skip */ }
      });
      await Promise.all(leaguePromises);

      // Build team info for each of the user's teams
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const userTeams: TeamInfo[] = userTeamEntries.map(({ team, role, position, isManager }) => {
        const teamInfo: TeamInfo = {
          team: {
            id: team.id,
            name: team.name,
            teamNumber: team.teamNumber,
            leagueId: team.leagueId,
          },
          role,
          position,
        };
        if (isManager) (teamInfo as any).isManager = true;

        // League info from cache
        const league = team.leagueId ? leagueCache.get(team.leagueId) : null;
        if (league) {
          teamInfo.team.league = { id: league.id, name: league.name };
        }

        // Next game from already-fetched matches
        const upcomingMatches = allMatches
          .filter((m: any) => {
            if (m.status !== 'scheduled') return false;
            if (m.homeTeamId !== team.id && m.awayTeamId !== team.id) return false;
            const matchDate = new Date(m.scheduledDate);
            matchDate.setHours(0, 0, 0, 0);
            return matchDate >= today;
          })
          .sort((a: any, b: any) => {
            const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
            const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
            return dateA.getTime() - dateB.getTime();
          });

        if (upcomingMatches.length > 0) {
          const nextMatch = upcomingMatches[0];
          const isHome = nextMatch.homeTeamId === team.id;
          const opponentId = isHome ? nextMatch.awayTeamId : nextMatch.homeTeamId;
          const opponentTeam = teamMap.get(opponentId);

          teamInfo.nextGame = {
            id: nextMatch.id,
            scheduledDate: nextMatch.scheduledDate,
            scheduledTime: nextMatch.scheduledTime || '',
            location: nextMatch.location,
            homeTeam: { id: nextMatch.homeTeamId, name: isHome ? team.name : (opponentTeam?.name || 'Unknown') },
            awayTeam: { id: nextMatch.awayTeamId, name: isHome ? (opponentTeam?.name || 'Unknown') : team.name },
            isHome,
            opponent: opponentTeam?.name || 'Unknown',
          };
        }

        // Player stats from already-fetched player data
        if (user.profileType === 'player' && role !== 'manager') {
          const stat = playerStats.find((s: any) => s.teamId === team.id);
          if (stat) {
            teamInfo.playerStats = {
              matchesPlayed: stat.matchesPlayed || 0,
              goals: stat.goals || 0,
              assists: stat.assists || 0,
              yellowCards: stat.yellowCards || 0,
              redCards: stat.redCards || 0,
            };
          }
        }

        // Team stats and league standings from cache
        if (league) {
          if (league.teamStatistics) {
            const teamStat = league.teamStatistics.find((s: any) => s.teamId === team.id);
            if (teamStat) {
              teamInfo.teamStats = {
                wins: teamStat.wins || 0,
                losses: teamStat.losses || 0,
                draws: teamStat.draws || 0,
                goalsFor: teamStat.goalsFor || 0,
                goalsAgainst: teamStat.goalsAgainst || 0,
                points: (teamStat.wins || 0) * 3 + (teamStat.draws || 0),
                goalDifference: (teamStat.goalsFor || 0) - (teamStat.goalsAgainst || 0),
              };
            }
          }

          if (league.teams && Array.isArray(league.teams) && league.teamStatistics) {
            teamInfo.leagueStandings = league.teams
              .map((t: any) => {
                const stat = league.teamStatistics.find((s: any) => s.teamId === t.id) || {
                  wins: 0, losses: 0, draws: 0, goalsFor: 0, goalsAgainst: 0,
                };
                const points = stat.wins * 3 + stat.draws;
                return {
                  teamId: t.id,
                  teamName: t.name || 'Unknown',
                  wins: stat.wins, losses: stat.losses, draws: stat.draws,
                  goalsFor: stat.goalsFor, goalsAgainst: stat.goalsAgainst,
                  points,
                  goalDifference: stat.goalsFor - stat.goalsAgainst,
                };
              })
              .sort((a: any, b: any) => b.points !== a.points ? b.points - a.points : b.goalDifference - a.goalDifference);
          }
        }

        return teamInfo;
      });

      setTeamsInfo(userTeams);
    } catch (error) {
      console.error('Error fetching user teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintNextGameRoster = async (teamInfo: TeamInfo) => {
    if (!teamInfo.nextGame) {
      alert('No upcoming games found');
      return;
    }

    const nextMatch = teamInfo.nextGame;
    const isHome = nextMatch.isHome;
    const teamType: 'home' | 'away' = isHome ? 'home' : 'away';

    try {
      const response = await api.get(`/matches/${nextMatch.id}/roster`);
      const { match, homeTeamPlayers, awayTeamPlayers } = response.data;
      
      await generateMatchRosterPDF(
        {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          scheduledDate: match.scheduledDate,
          scheduledTime: match.scheduledTime,
          location: match.location,
          league: match.league
        },
        homeTeamPlayers,
        awayTeamPlayers,
        teamType
      );
    } catch (error: any) {
      console.error('Error generating roster PDF:', error);
      alert(error.response?.data?.error || 'Failed to generate roster PDF');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  // Show different content based on user type
  const allowedTypes = ['player', 'manager', 'referee', 'parent', 'admin', 'super_admin', 'director'];
  if (!user || !allowedTypes.includes(user.profileType)) {
    return (
      <div className="container">
        <h1>Welcome, {user?.firstName}!</h1>
        <p>Dashboard customization is available for players, managers, referees, parents, directors, and admins.</p>
      </div>
    );
  }

  // Admin-specific dashboard content
  if (isAdminOrSuperAdmin(user.profileType)) {
    return <AdminDashboard user={user} />;
  }

  return (
    <div className="container">
      <h1>Welcome, {user?.firstName}!</h1>
      <p style={{ color: '#757575', marginBottom: '30px' }}>
        Profile Type: <strong>{user?.profileType}</strong>
      </p>

      {teamsInfo.length === 0 ? (
        <div className="card">
          <p>You are not currently associated with any teams.</p>
          <Link to="/teams" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '15px' }}>
            Browse Teams
          </Link>
        </div>
      ) : (
        teamsInfo.map((teamInfo, idx) => {
          const isManager = (teamInfo as any).isManager || teamInfo.role === 'manager';
          const isAdmin = isAdminOrSuperAdmin(user?.profileType);
          const hasPermission = isManager || isAdmin;
          
          return (
          <div key={teamInfo.team.id} className="card" style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2>
                  <Link to={`/teams/${teamInfo.team.id}`} style={{ color: '#2E7D32', textDecoration: 'none' }}>
                    {teamInfo.team.name} ({teamInfo.team.teamNumber})
                  </Link>
                </h2>
                {hasPermission && teamInfo.nextGame && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handlePrintNextGameRoster(teamInfo)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', padding: '8px 15px' }}
                    >
                      📄 Print Next Game Roster
                    </button>
                  </div>
                )}
                <p style={{ color: '#757575', margin: '5px 0' }}>
                  <strong>Your Role:</strong> <span className="badge badge-info">{teamInfo.role}</span>
                  {teamInfo.position && (
                    <>
                      {' '} | <strong>Position:</strong> {teamInfo.position}
                    </>
                  )}
                </p>
                {teamInfo.team.league && (
                  <p style={{ color: '#757575', margin: '5px 0' }}>
                    <strong>League:</strong>{' '}
                    <Link to={`/leagues?leagueId=${teamInfo.team.league.id}`} style={{ color: '#2E7D32' }}>
                      {teamInfo.team.league.name}
                    </Link>
                  </p>
                )}
              </div>
              <Link to={`/teams/${teamInfo.team.id}`} className="btn btn-primary">
                View Team
              </Link>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              {/* Next Game */}
              <div className="card" style={{ background: '#F5F5F5' }}>
                <h3 style={{ marginBottom: '15px' }}>Next Game</h3>
                {teamInfo.nextGame ? (
                  <div>
                    <p><strong>Date:</strong> {new Date(teamInfo.nextGame.scheduledDate).toLocaleDateString()}</p>
                    <p><strong>Time:</strong> {teamInfo.nextGame.scheduledTime || 'TBD'}</p>
                    <p><strong>Location:</strong> {teamInfo.nextGame.location}</p>
                    <p><strong>Opponent:</strong> {teamInfo.nextGame.isHome ? 'vs' : '@'} {teamInfo.nextGame.opponent}</p>
                    <div style={{ marginTop: '15px' }}>
                      <Link 
                        to={`/matches`} 
                        className="btn btn-secondary" 
                        style={{ fontSize: '14px', padding: '8px 15px', display: 'inline-block' }}
                      >
                        View Match Details
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#757575' }}>No upcoming games scheduled</p>
                )}
              </div>

              {/* Stats */}
              <div className="card" style={{ background: '#F5F5F5' }}>
                <h3 style={{ marginBottom: '15px' }}>Current Stats</h3>
                {user.profileType === 'player' && teamInfo.playerStats ? (
                  <div>
                    <p><strong>Matches Played:</strong> {teamInfo.playerStats.matchesPlayed}</p>
                    <p><strong>Goals:</strong> {teamInfo.playerStats.goals}</p>
                    <p><strong>Assists:</strong> {teamInfo.playerStats.assists}</p>
                    <p><strong>Yellow Cards:</strong> {teamInfo.playerStats.yellowCards}</p>
                    <p><strong>Red Cards:</strong> {teamInfo.playerStats.redCards}</p>
                  </div>
                ) : teamInfo.teamStats ? (
                  <div>
                    <p><strong>Wins:</strong> {teamInfo.teamStats.wins}</p>
                    <p><strong>Losses:</strong> {teamInfo.teamStats.losses}</p>
                    <p><strong>Draws:</strong> {teamInfo.teamStats.draws}</p>
                    <p><strong>Goals For:</strong> {teamInfo.teamStats.goalsFor}</p>
                    <p><strong>Goals Against:</strong> {teamInfo.teamStats.goalsAgainst}</p>
                    <p><strong>Points:</strong> {teamInfo.teamStats.points}</p>
                    <p><strong>Goal Difference:</strong> {teamInfo.teamStats.goalDifference > 0 ? '+' : ''}{teamInfo.teamStats.goalDifference}</p>
                  </div>
                ) : (
                  <p style={{ color: '#757575' }}>No statistics available yet</p>
                )}
              </div>
            </div>

            {/* League Standings */}
            {teamInfo.leagueStandings && teamInfo.leagueStandings.length > 0 && (
              <LeagueStandingsTable 
                standings={teamInfo.leagueStandings} 
                teamId={teamInfo.team.id}
                leagueName={teamInfo.team.league?.name}
              />
            )}
          </div>
        );
        })
      )}
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard: React.FC<{ user: any }> = ({ user }) => {
  const [upcomingGames, setUpcomingGames] = useState<any[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<any[]>([]);
  const [pendingTeams, setPendingTeams] = useState<any[]>([]);
  const [pendingJoinRequests, setPendingJoinRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsPage, setTeamsPage] = useState(1);
  const [joinRequestsPage, setJoinRequestsPage] = useState(1);
  const [teamsPagination, setTeamsPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [joinRequestsPagination, setJoinRequestsPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchAdminData();
  }, [teamsPage, joinRequestsPage]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUpcomingGames(),
        fetchBlackoutDates(),
        fetchPendingTeams(),
        fetchPendingJoinRequests()
      ]);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingGames = async () => {
    try {
      const response = await api.get('/matches', { params: { page: '1', limit: '100', status: 'scheduled' } });
      const matches = response.data.matches || response.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const upcoming = matches
        .filter((m: any) => {
          const matchDate = new Date(m.scheduledDate);
          matchDate.setHours(0, 0, 0, 0);
          return matchDate >= today;
        })
        .sort((a: any, b: any) => {
          const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
          const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 10);

      // Enrich with league and team info
      const enrichedGames = await Promise.all(
        upcoming.map(async (match: any) => {
          let league = null;
          if (match.leagueId) {
            try {
              const leagueRes = await api.get(`/leagues/${match.leagueId}`);
              league = { id: leagueRes.data.id, name: leagueRes.data.name };
            } catch (err) {
              console.error('Error fetching league:', err);
            }
          }

          return {
            ...match,
            league
          };
        })
      );

      setUpcomingGames(enrichedGames);
    } catch (error) {
      console.error('Error fetching upcoming games:', error);
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      const response = await api.get('/seasons', { params: { page: '1', limit: '100' } });
      const seasons = response.data.seasons || response.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fourteenDaysFromNow = new Date();
      fourteenDaysFromNow.setDate(today.getDate() + 14);

      const upcomingBlackouts: any[] = [];

      seasons.forEach((season: any) => {
        if (season.blackoutPeriods && Array.isArray(season.blackoutPeriods)) {
            season.blackoutPeriods.forEach((period: any) => {
            if (!period.startDate || !period.endDate) return;
            
            const startDate = new Date(period.startDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(period.endDate);
            endDate.setHours(0, 0, 0, 0);

            // Check if any part of the blackout period is within 14 days
            // A blackout is "upcoming" if it starts within 14 days or is currently ongoing
            if ((startDate >= today && startDate <= fourteenDaysFromNow) || 
                (startDate <= today && endDate >= today)) {
              upcomingBlackouts.push({
                ...period,
                seasonName: season.name,
                leagueId: season.leagueId,
                league: season.league
              });
            }
          });
        }
      });

      // Sort by start date
      upcomingBlackouts.sort((a, b) => {
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });

      setBlackoutDates(upcomingBlackouts);
    } catch (error) {
      console.error('Error fetching blackout dates:', error);
    }
  };

  const fetchPendingTeams = async () => {
    try {
      const response = await api.get('/teams/pending/approval');
      const allPending = response.data || [];
      
      // Client-side pagination
      const pageNum = teamsPage;
      const limitNum = 10;
      const startIndex = (pageNum - 1) * limitNum;
      const endIndex = startIndex + limitNum;
      const paginated = allPending.slice(startIndex, endIndex);

      setPendingTeams(paginated);
      setTeamsPagination({
        page: pageNum,
        limit: limitNum,
        total: allPending.length,
        totalPages: Math.ceil(allPending.length / limitNum)
      });
    } catch (error) {
      console.error('Error fetching pending teams:', error);
    }
  };

  const fetchPendingJoinRequests = async () => {
    try {
      const response = await api.get('/team-join-requests/pending');
      const allPending = response.data || [];
      
      // Client-side pagination
      const pageNum = joinRequestsPage;
      const limitNum = 10;
      const startIndex = (pageNum - 1) * limitNum;
      const endIndex = startIndex + limitNum;
      const paginated = allPending.slice(startIndex, endIndex);

      setPendingJoinRequests(paginated);
      setJoinRequestsPagination({
        page: pageNum,
        limit: limitNum,
        total: allPending.length,
        totalPages: Math.ceil(allPending.length / limitNum)
      });
    } catch (error) {
      console.error('Error fetching pending join requests:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Welcome, {user.firstName}!</h1>
      <p style={{ color: '#757575', marginBottom: '30px' }}>
        Admin Dashboard - Overview of upcoming games, blackout dates, and pending tasks
      </p>

      {/* Upcoming Games */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <h2>Next 10 Upcoming Games</h2>
        {upcomingGames.length === 0 ? (
          <p style={{ color: '#757575' }}>No upcoming games scheduled.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Home Team</th>
                  <th>Away Team</th>
                  <th>League</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {upcomingGames.map((game) => (
                  <tr key={game.id}>
                    <td>{new Date(game.scheduledDate).toLocaleDateString()}</td>
                    <td>{game.scheduledTime || 'TBD'}</td>
                    <td>{game.homeTeam?.name || 'TBD'}</td>
                    <td>{game.awayTeam?.name || 'TBD'}</td>
                    <td>{game.league?.name || 'N/A'}</td>
                    <td>{game.location || 'TBD'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Blackout Dates */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <h2>Upcoming Blackout Dates (Within 14 Days)</h2>
        {blackoutDates.length === 0 ? (
          <p style={{ color: '#757575' }}>No blackout dates within the next 14 days.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Reason</th>
                  <th>Season</th>
                  <th>League</th>
                </tr>
              </thead>
              <tbody>
                {blackoutDates.map((blackout, idx) => (
                  <tr key={blackout.id || idx}>
                    <td>{new Date(blackout.startDate).toLocaleDateString()}</td>
                    <td>{new Date(blackout.endDate).toLocaleDateString()}</td>
                    <td>{blackout.reason || 'N/A'}</td>
                    <td>{blackout.seasonName || 'N/A'}</td>
                    <td>{blackout.league?.name || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Team Approvals */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: 0 }}>Pending Team Approvals</h2>
          <Link to="/teams/approvals" className="btn btn-secondary">
            View All
          </Link>
        </div>
        {pendingTeams.length === 0 ? (
          <p style={{ color: '#757575' }}>No pending team approvals.</p>
        ) : (
          <>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              {pendingTeams.map((team) => (
                <div key={team.id} className="card" style={{ border: '1px solid #E0E0E0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: team.colors?.primary || '#ccc',
                        border: `3px solid ${team.colors?.secondary || '#999'}`
                      }}
                    />
                    <div>
                      <h3 style={{ margin: 0 }}>{team.name}</h3>
                      <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>{team.teamNumber}</p>
                    </div>
                  </div>
                  <p><strong>Manager:</strong> {team.manager ? `${team.manager.firstName} ${team.manager.lastName}` : 'N/A'}</p>
                  <p><strong>Members:</strong> {team.memberCount || 0}</p>
                  <Link to="/teams/approvals" className="btn btn-primary" style={{ marginTop: '10px', width: '100%' }}>
                    Review
                  </Link>
                </div>
              ))}
            </div>
            {teamsPagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setTeamsPage(teamsPage - 1)}
                  disabled={teamsPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {teamsPagination.page} of {teamsPagination.totalPages} ({teamsPagination.total} total)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setTeamsPage(teamsPage + 1)}
                  disabled={teamsPage >= teamsPagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pending Join Requests */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: 0 }}>Pending Join Requests</h2>
          <Link to="/team-join-approvals" className="btn btn-secondary">
            View All
          </Link>
        </div>
        {pendingJoinRequests.length === 0 ? (
          <p style={{ color: '#757575' }}>No pending join requests.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Team</th>
                    <th>League</th>
                    <th>Requested</th>
                    <th>Message</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingJoinRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        {request.player ? `${request.player.firstName} ${request.player.lastName}` : 'Unknown'}
                      </td>
                      <td>{request.team?.name || 'N/A'}</td>
                      <td>{request.league?.name || 'N/A'}</td>
                      <td>{new Date(request.requestedAt).toLocaleDateString()}</td>
                      <td>{request.message || 'N/A'}</td>
                      <td>
                        <Link to="/team-join-approvals" className="btn btn-sm btn-primary">
                          Review
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {joinRequestsPagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setJoinRequestsPage(joinRequestsPage - 1)}
                  disabled={joinRequestsPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {joinRequestsPagination.page} of {joinRequestsPagination.totalPages} ({joinRequestsPagination.total} total)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setJoinRequestsPage(joinRequestsPage + 1)}
                  disabled={joinRequestsPage >= joinRequestsPagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
